import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  BookOpenIcon,
  UserIcon,
  ChevronRightIcon,
  CalendarIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import EditLeadAssociationModal from './modals/EditLeadAssociationModal';

interface LeadAssociation {
  id: number;
  lead_id: number;
  lead_name: string;
  lead_email: string;
  session_id?: number;
  session_name?: string;
  session_code?: string;
  university_id: number;
  university_name: string;
  university_location?: string;
  campus_id?: number;
  campus_name?: string;
  campus_location?: string;
  course_id?: number;
  course_name?: string;
  course_code?: string;
  country_id?: number;
  country_name?: string;
  country_code?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

interface AssociatedLeadsTabProps {
  onRefresh?: () => void;
}

const AssociatedLeadsTab: React.FC<AssociatedLeadsTabProps> = ({ onRefresh }) => {
  const [associations, setAssociations] = useState<LeadAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'session' | 'university' | 'campus' | 'course'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'leadName' | 'universityName'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [selectedAssociation, setSelectedAssociation] = useState<LeadAssociation | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState<LeadAssociation | null>(null);

  const pageSize = 20;

  useEffect(() => {
    fetchAssociations();
  }, [currentPage, searchQuery, filterType, sortBy, sortOrder]);

  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: searchQuery,
        sortBy,
        sortOrder
      });

      const response = await api.get(`/lead-associations?${params.toString()}`);
      
      if (response.data.success) {
        const associationsData = response.data.data || [];
        setAssociations(associationsData);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
      } else {
        setError('Failed to fetch associations');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching associations');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (association: LeadAssociation) => {
    setSelectedAssociation(association);
    setShowDetailsModal(true);
  };

  const handleEdit = (association: LeadAssociation) => {
    setEditingAssociation(association);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    fetchAssociations();
    onRefresh?.();
  };

  const handleToggleStatus = async (associationId: number, currentStatus: boolean) => {
    setUpdatingIds(prev => new Set(prev).add(associationId));

    try {
      await api.patch('/lead-associations/bulk-status', {
        associationIds: [associationId],
        isActive: !currentStatus
      });

      // Update local state
      setAssociations(prev =>
        prev.map(assoc =>
          assoc.id === associationId ? { ...assoc, is_active: !currentStatus } : assoc
        )
      );
      onRefresh?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update association status');
    } finally {
      setUpdatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(associationId);
        return newSet;
      });
    }
  };

  const handleDelete = async (associationId: number) => {
    if (!window.confirm('Are you sure you want to delete this association?')) {
      return;
    }

    setDeletingIds(prev => new Set(prev).add(associationId));
    
    try {
      await api.delete(`/lead-associations/${associationId}`);
      setAssociations(prev => prev.filter(assoc => assoc.id !== associationId));
      setTotalRecords(prev => prev - 1);
      onRefresh?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting association');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(associationId);
        return newSet;
      });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAssociations();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const filteredAssociations = associations.filter(assoc => {
    if (filterType === 'session') return assoc.session_id;
    if (filterType === 'university') return true;
    if (filterType === 'campus') return assoc.campus_id;
    if (filterType === 'course') return assoc.course_id;
    return true;
  });

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return 'No date';
    
    try {
      // Handle different date formats
      let date;
      
      if (typeof dateString === 'string') {
        // Handle PostgreSQL timestamp format (e.g., "2024-01-15 14:30:00")
        if (dateString.includes(' ')) {
          // Replace space with 'T' for ISO format
          const isoString = dateString.replace(' ', 'T');
          date = new Date(isoString);
        } else {
          date = new Date(dateString);
        }
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString, 'Type:', typeof dateString);
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Date string:', dateString, 'Type:', typeof dateString);
      return 'Invalid date';
    }
  };

  const getAssociationType = (assoc: LeadAssociation) => {
    if (assoc.course_id) return 'Course';
    if (assoc.campus_id) return 'Campus';
    if (assoc.session_id) return 'Session';
    return 'University';
  };

  const getAssociationPath = (assoc: LeadAssociation) => {
    let path = '';
    if (assoc.session_name) path += `${assoc.session_name} → `;
    path += assoc.university_name;
    if (assoc.campus_name) path += ` → ${assoc.campus_name}`;
    if (assoc.course_name) path += ` → ${assoc.course_name}`;
    return path;
  };

  if (loading && associations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading associations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Associated Leads</h2>
          <p className="text-gray-600">
            {totalRecords} lead{totalRecords !== 1 ? 's' : ''} associated with university entities
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads or universities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filter Type */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Associations</option>
              <option value="session">Session Level</option>
              <option value="university">University Only</option>
              <option value="campus">Campus Level</option>
              <option value="course">Course Level</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="created_at">Sort by Date</option>
              <option value="leadName">Sort by Lead Name</option>
              <option value="universityName">Sort by University</option>
            </select>

            {/* Sort Order */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Search
            </button>
            
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear Filters
            </button>
          </div>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Associations List */}
      {filteredAssociations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No associations found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery ? 'No associations match your search criteria.' : 'No leads have been associated with university entities yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    University Association
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Association Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssociations.map((association) => {
                  const isActive = association.is_active !== false;
                  const isUpdating = updatingIds.has(association.id);
                  return (
                  <tr 
                    key={association.id} 
                    className={`hover:bg-gray-50 ${isActive ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {association.lead_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {association.lead_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {association.session_name && (
                          <div className="flex items-center mb-1">
                            <CalendarIcon className="h-4 w-4 text-blue-500 mr-2" />
                            {association.session_name}
                            {association.session_code && (
                              <span className="text-gray-500 ml-1">({association.session_code})</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-4 w-4 text-green-500 mr-2" />
                          {association.university_name}
                        </div>
                        {association.campus_name && (
                          <div className="text-sm text-gray-600 ml-6">
                            <MapPinIcon className="h-3 w-3 text-purple-500 mr-1 inline" />
                            {association.campus_name}
                          </div>
                        )}
                        {association.course_name && (
                          <div className="text-sm text-gray-600 ml-6">
                            <BookOpenIcon className="h-3 w-3 text-orange-500 mr-1 inline" />
                            {association.course_name}
                            {association.course_code && (
                              <span className="text-gray-500"> ({association.course_code})</span>
                            )}
                          </div>
                        )}
                        {association.country_name && (
                          <div className="text-sm text-gray-600 ml-6">
                            <GlobeAltIcon className="h-3 w-3 text-teal-500 mr-1 inline" />
                            {association.country_name}
                            {association.country_code && (
                              <span className="text-gray-500"> ({association.country_code})</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {association.course_id ? (
                          <BookOpenIcon className="h-5 w-5 text-orange-500 mr-2" />
                        ) : association.campus_id ? (
                          <MapPinIcon className="h-5 w-5 text-purple-500 mr-2" />
                        ) : association.session_id ? (
                          <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
                        ) : (
                          <BuildingOfficeIcon className="h-5 w-5 text-green-500 mr-2" />
                        )}
                        <span className="text-sm text-gray-900">
                          {getAssociationType(association)}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={() => handleToggleStatus(association.id, isActive)}
                          disabled={isUpdating}
                          className="sr-only peer"
                        />
                        <div className={`relative w-11 h-6 rounded-full peer transition-colors duration-200 ease-in-out ${
                          isActive 
                            ? 'bg-green-500' 
                            : 'bg-gray-300'
                        } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 ${
                          isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}>
                          <div className={`absolute top-0.5 left-0.5 bg-white border-gray-300 rounded-full h-5 w-5 transition-transform duration-200 ease-in-out ${
                            isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}></div>
                        </div>
                        <span className={`ml-3 text-sm font-medium ${
                          isActive ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                        {isUpdating && (
                          <span className="ml-2">
                            <span className="animate-spin inline-block h-3 w-3 border-2 border-current border-t-transparent rounded-full"></span>
                          </span>
                        )}
                      </label>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {association.created_at ? formatDate(association.created_at) : 'No date'}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewDetails(association)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View Details
                        </button>
                        <button
                          onClick={() => handleEdit(association)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(association.id)}
                          disabled={deletingIds.has(association.id)}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingIds.has(association.id) ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {((currentPage - 1) * pageSize) + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalRecords)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{totalRecords}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => 
                          page === 1 || 
                          page === totalPages || 
                          Math.abs(page - currentPage) <= 2
                        )
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAssociation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Association Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                {/* Lead Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Lead Information
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Name</label>
                        <p className="text-sm text-gray-900">{selectedAssociation.lead_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{selectedAssociation.lead_email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Lead ID</label>
                        <p className="text-sm text-gray-900">#{selectedAssociation.lead_id}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* University Association */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-green-600 mr-2" />
                    University Association
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-3">
                      {selectedAssociation.session_name && (
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-blue-500 mr-2" />
                          <div>
                            <label className="text-sm font-medium text-gray-500">Session</label>
                            <p className="text-sm text-gray-900">
                              {selectedAssociation.session_name}
                              {selectedAssociation.session_code && (
                                <span className="text-gray-500 ml-1">({selectedAssociation.session_code})</span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-green-500 mr-2" />
                        <div>
                          <label className="text-sm font-medium text-gray-500">University</label>
                          <p className="text-sm text-gray-900">{selectedAssociation.university_name}</p>
                        </div>
                      </div>
                      
                      {selectedAssociation.campus_name && (
                        <div className="flex items-center ml-6">
                          <MapPinIcon className="h-4 w-4 text-purple-500 mr-2" />
                          <div>
                            <label className="text-sm font-medium text-gray-500">Campus</label>
                            <p className="text-sm text-gray-900">{selectedAssociation.campus_name}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedAssociation.course_name && (
                        <div className="flex items-center ml-6">
                          <BookOpenIcon className="h-4 w-4 text-orange-500 mr-2" />
                          <div>
                            <label className="text-sm font-medium text-gray-500">Course</label>
                            <p className="text-sm text-gray-900">
                              {selectedAssociation.course_name}
                              {selectedAssociation.course_code && (
                                <span className="text-gray-500"> ({selectedAssociation.course_code})</span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {selectedAssociation.country_name && (
                        <div className="flex items-center ml-6">
                          <GlobeAltIcon className="h-4 w-4 text-teal-500 mr-2" />
                          <div>
                            <label className="text-sm font-medium text-gray-500">Country</label>
                            <p className="text-sm text-gray-900">
                              {selectedAssociation.country_name}
                              {selectedAssociation.country_code && (
                                <span className="text-gray-500"> ({selectedAssociation.country_code})</span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Association Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ChevronRightIcon className="h-5 w-5 text-gray-600 mr-2" />
                    Association Details
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Association Type</label>
                        <p className="text-sm text-gray-900">{getAssociationType(selectedAssociation)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Created</label>
                        <p className="text-sm text-gray-900">
                          {selectedAssociation.created_at ? formatDate(selectedAssociation.created_at) : 'No date'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Association ID</label>
                        <p className="text-sm text-gray-900">#{selectedAssociation.id}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditLeadAssociationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingAssociation(null);
        }}
        association={editingAssociation}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default AssociatedLeadsTab;
